import { useState, useEffect, useRef, useCallback } from 'react';

export const useWorkoutTimer = (workoutProgram, onComplete) => {
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalElapsed, setTotalElapsed] = useState(0);
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [distanceTraveled, setDistanceTraveled] = useState(0);

  const intervalRef = useRef(null);
  // Folosim un ref pentru onComplete ca să nu re-declanșăm useEffect dacă funcția se schimbă
  const onCompleteRef = useRef(onComplete);

  // Actualizăm ref-ul când onComplete se schimbă
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const phases = workoutProgram?.phases || [];
  const currentPhase = phases[currentPhaseIndex];
  const totalDuration = (workoutProgram?.total_time_min || 0) * 60;

  useEffect(() => {
    if (workoutProgram && phases.length > 0 && !isActive && totalElapsed === 0) {
      setTimeRemaining(phases[0].duration_min * 60);
      setCurrentPhaseIndex(0);
      setCurrentSpeed(phases[0].startSpeed_kmh);
      setDistanceTraveled(0);
    }
  }, [workoutProgram]);

  const tick = useCallback(() => {
    // 1. Calculăm distanța
    setDistanceTraveled(prevDist => {
      const metersPerSecond = currentSpeed / 3.6;
      return prevDist + metersPerSecond;
    });

    // 2. Gestionăm timpul
    setTimeRemaining(prev => {
      if (prev <= 1) {
        const nextIndex = currentPhaseIndex + 1;
        if (nextIndex < phases.length) {
          setCurrentPhaseIndex(nextIndex);
          setCurrentSpeed(phases[nextIndex].startSpeed_kmh);
          return phases[nextIndex].duration_min * 60;
        } else {
          // FINAL PROGRAM
          setIsActive(false);
          setIsPaused(false);
          
          // Trigger callback dacă există
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
          
          return 0;
        }
      }
      return prev - 1;
    });

    setTotalElapsed(prev => prev + 1);
  }, [currentPhaseIndex, phases, currentSpeed]);

  useEffect(() => {
    if (isActive && !isPaused) {
      intervalRef.current = setInterval(tick, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isActive, isPaused, tick]);

  const handleStart = () => {
    if (!workoutProgram) return;
    setIsActive(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    if (!isActive) return;
    setIsPaused(!isPaused);
  };

  const handleStop = () => {
    setIsActive(false);
    setIsPaused(false);
    setTotalElapsed(0);
    setCurrentPhaseIndex(0);
    setDistanceTraveled(0);
    if (phases.length > 0) {
      setTimeRemaining(phases[0].duration_min * 60);
      setCurrentSpeed(phases[0].startSpeed_kmh);
    }
  };

  const adjustSpeed = (amount) => {
    setCurrentSpeed(prev => {
      const newSpeed = parseFloat((prev + amount).toFixed(1));
      return newSpeed > 0 ? newSpeed : 0.5;
    });
  };

  const progressPercentage = totalDuration > 0 
    ? Math.min(100, (totalElapsed / totalDuration) * 100) 
    : 0;

  return {
    isActive,
    isPaused,
    currentPhase,
    currentPhaseIndex,
    timeRemaining,
    totalElapsed,
    totalDuration,
    progressPercentage,
    currentSpeed,
    distanceTraveled,
    handleStart,
    handlePause,
    handleStop,
    adjustSpeed
  };
};